import './UISlot.css'

import { useCallback, useEffect, useState } from 'react'
import { Router, Switch, Route } from 'react-router-dom'
import { SDKAction } from '@squareup/sq-addons-web-sdk'
import { useToast } from 'hooks/useToast'
import ConnectionErrorScreen from 'components/ConnectionError'
import LoadingScreen from 'components/Loading'
import MainScreen from 'screens/Main'
import { setScannedCode } from 'reducers/barcodeScanner'
import { RequestContext } from 'utils/pos'
import { useAppDispatch, useAppSelector } from 'store'
import { useGetCustomer } from 'hooks/customer'
import { useInitSdk, usePosContext } from 'hooks/pos'
import { t } from 'react-i18nify'
import { History } from 'history'
import SearchCustomersScreen from 'screens/SearchCustomers'
import CustomerScreen from 'screens/Customer'
import ValidateOfferScreen from 'screens/ValidateOffer'
import RemoveInvalidOfferScreen from 'screens/RemoveInvalidOffer'
import { getOrderCustomer } from 'utils/customer'

type Props = {
  browserHistory: History<unknown>
}

const UISlot = ({ browserHistory }: Props) => {
  const [showToast] = useToast()
  const dispatch = useAppDispatch()

  const context = usePosContext()

  const { initializeSdk } = useInitSdk()
  const { getCustomer } = useGetCustomer()
  const scannedCode = useAppSelector(state => state.barcodeScanner.scannedCode)

  const [error, setError] = useState<Error | null>(null)

  const onScanCode = useCallback(
    async (code: string) => {
      const customer = await getOrderCustomer()
      if (customer) {
        showToast({
          message: t('general.customerAlreadyAdded'),
          persistent: false,
          variant: 'warning',
        })
        return
      }

      if (code === scannedCode) return

      dispatch(setScannedCode(code))

      const result = await getCustomer(RequestContext.BARCODE_SCANNER, code)
      if (result.isErr()) {
        showToast({
          message: t('general.invalidBarcode'),
          persistent: false,
          variant: 'warning',
        })
        return
      }

      browserHistory.push('/customer' + browserHistory.location.search)
    },
    [browserHistory, dispatch, getCustomer, scannedCode, showToast]
  )

  const initSdk = useCallback(async () => {
    const result = await initializeSdk({
      [SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED]: onScanCode,
    })

    if (result.isErr()) {
      setError(result.error)
    }
  }, [initializeSdk, onScanCode])

  useEffect(() => {
    initSdk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return <ConnectionErrorScreen onClickTryAgain={initSdk} />
  }

  if (!context) {
    return <LoadingScreen />
  }

  return (
    <div className="page-container">
      <Router history={browserHistory}>
        <Switch>
          <Route path="/invalid-discounts">
            <RemoveInvalidOfferScreen />
          </Route>
          <Route path="/checkout">
            <ValidateOfferScreen />
          </Route>
          <Route path="/validate-offer">
            <ValidateOfferScreen />
          </Route>
          <Route path="/customer">
            <CustomerScreen />
          </Route>
          <Route path="/search-customers">
            <SearchCustomersScreen />
          </Route>
          <Route path="/">
            <MainScreen />
          </Route>
        </Switch>
      </Router>
    </div>
  )
}

export default UISlot
